import {
  ASSIGNED_SEATING,
  ASSIGNED_SEATING_MODES,
  enums,
  optionsAsList,
} from "@shared/constants";
import { SEATING_LOCATION_TYPE_MAP } from "@shared/constants/enums";
import { TicketItemGroupPermissionTypeLocation } from "@shared/modules/item-groups/types/ticket-item-group-type";
import Konva from "konva";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import GeneralAdmissionFactory from "../factories/GeneralAdmissionFactory";
import LocationFactory from "../factories/LocationFactory";
import SeatFactory from "../factories/SeatFactory";
import SegmentFactory from "../factories/SegmentFactory";
import {
  LocationDataType,
  MapPermissionsType,
  PositionType,
  PublicBaseLocationSerializer,
  PublicBaseSegmentSetSerializer,
  PublicBaseSpaceSerializer,
  SeatHighlightData,
  SeatWithLocationDataType,
} from "../types/seating-types";
import SeatingService from "./SeatingService";

type ExtendedKonvaStage = Konva.Stage & {
  backgroundImageLoaded: boolean;
  clearSelections: () => void;
  checkCollision: (display1: Konva.Node, display2: Konva.Node) => boolean;
  startSelectDrag: (posIn: PositionType) => void;
  updateSelectDrag: (posIn: PositionType) => void;
};

type ExtendedKonvaGroup = Konva.Group & {
  $segmentData: PublicBaseSegmentSetSerializer;
};

type ExtendedKonvaContainer = Konva.Container & {
  $segmentData: PublicBaseSegmentSetSerializer;
};

type BoundsType = { x: number; y: number; width: number; height: number };

export default class StageService {
  public isForPrint = false;

  private MAX_SCALE = 2;

  private MIN_SCALE = 0.01;

  private handleMouseMove: (input: unknown) => void = () => null;

  private GRID_WIDTH: number = ASSIGNED_SEATING.EDITOR.GRID.GRID_WIDTH;

  public maxCountToDisplayAllSeats: number =
    ASSIGNED_SEATING.OPTIONS.MAX_COUNT_TO_DISPLAY_ALL_SEATS;

  private maxCountToDisplaySeatsOnAndroid: number =
    ASSIGNED_SEATING.OPTIONS.MAX_COUNT_TO_DISPLAY_SEATS_ON_ANDROID;

  public seatSize = 0;

  public seatSpacing = 0;

  public displayAllSeats = false;

  private hideSegmentBackground = false;

  private isMultiSelection = false;

  private isPinchingGesture = false;

  private lastDist = 0;
  private scaleForHighlight = 1;
  private gaLocationsHovered: number[] = [];

  private activeSegment: number | null = null; // used only when enforcing shouldNotDisplayAllSeats

  // contains the most recent segment that was hovered over with the mouse
  // controls which segment is highlighted on the map
  private hoveredSegment: Konva.Node | null = null;

  private toolMode = "";

  // keeps track of if the mouse was previously hovered over a seat that enables related seats
  // prevents conflicting displays for selecting enabled seats versus hovering over enabled seats
  // also prevents enableSeatsById from running on each render
  // example usage: resale seats
  private isSeatFilterEnabled = false;

  // keeps track of if the user has selected a seat that requires a minimum number of tickets to be selected before proceeding
  // prevents conflicting displays for selecting enabled seats versus hovering over enabled seats
  // also prevents enableSeatsByItemType from running on each seat selection
  // example usage: minimum purchase seats
  public isLimitedSeatSelectionEnabled = false;

  // tracks if the best available list is open
  private isBestAvailableListOpen = false;

  private seatsInViewportTimer: NodeJS.Timeout | undefined;

  private manageLayersTimer: NodeJS.Timeout | undefined;

  private scrollTimer: NodeJS.Timeout | undefined;

  public stage: ExtendedKonvaStage = {} as ExtendedKonvaStage;

  public space: PublicBaseSpaceSerializer | undefined;

  public layers: {
    background: Konva.Layer;
    seatsAvailable: Konva.Layer;
    seatsDisabled: Konva.Layer;
    segments: Konva.Layer;
    mainLayer: Konva.Layer;
    alwaysDisplaySeatsAndLocations: boolean;
  } = {
    background: {} as Konva.Layer,
    seatsAvailable: {} as Konva.Layer,
    seatsDisabled: {} as Konva.Layer,
    segments: {} as Konva.Layer,
    mainLayer: {} as Konva.Layer,
    alwaysDisplaySeatsAndLocations: false,
  };

  public elementId: string | undefined;

  public seatingService: SeatingService;

  public locationFactory: LocationFactory;

  public seatFactory: SeatFactory;

  public generalAdmissionFactory: GeneralAdmissionFactory;

  public segmentFactory: SegmentFactory;

  private posStartSelection: PositionType = { x: 0, y: 0 };

  private posNowSelection: PositionType | undefined;

  private onActiveSegmentChange:
    | ((segmentId: number | null) => void)
    | undefined;
  private showCheckmarkOnSeat = true;

  private posRect:
    | { x1: number; y1: number; x2: number; y2: number }
    | undefined;

  private lastSelectionWasIndividual = false; // Add this property to track selection type

  constructor(seatingService: SeatingService) {
    this.seatingService = seatingService;
    this.segmentFactory = new SegmentFactory(seatingService);
    this.seatFactory = new SeatFactory(seatingService);
    this.generalAdmissionFactory = new GeneralAdmissionFactory(seatingService);
    this.locationFactory = new LocationFactory(
      seatingService,
      this.generalAdmissionFactory,
      this.seatFactory
    );
    this.init = this.init.bind(this);
    this.centerStage = this.centerStage.bind(this);
    this.setMouseMove = this.setMouseMove.bind(this);
    this.shouldNotDisplayAllSeats = this.shouldNotDisplayAllSeats.bind(this);
    this.getSeatData = this.getSeatData.bind(this);
    this.setLocationSelected = this.setLocationSelected.bind(this);
    this.cullView = this.cullView.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  setActiveSegment(segmentId: number | null, shouldFilter = false): void {
    this.activeSegment = segmentId;
    if (shouldFilter) {
      this.segmentFactory.handleSegmentClick(segmentId);
    }
  }

  setBestAvailableListOpen(isOpen: boolean): void {
    this.isBestAvailableListOpen = isOpen;
  }

  isItemAssigner(): boolean {
    return (
      this.seatingService.mode === ASSIGNED_SEATING_MODES.EDIT_SEAT_TICKETS.id
    );
  }

  static isAndroidDevice(): boolean {
    return navigator.userAgent.indexOf("Android") !== -1;
  }

  /**
   * @returns if the number of seats is over the threshold to display all seats
   */
  shouldNotDisplayAllSeats(): boolean {
    let returnVal = true;
    if (this.space) {
      // android devices have a smaller seat threshold than other devices
      if (StageService.isAndroidDevice() && !this.isBestAvailableListOpen) {
        // Only apply Android threshold if best available list is open
        returnVal =
          this.space.seat_count >= this.maxCountToDisplaySeatsOnAndroid;
      } else {
        returnVal = this.space.seat_count >= this.maxCountToDisplayAllSeats;
      }
    }
    return returnVal;
  }

  selectionRect: Konva.Rect = new Konva.Rect({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    stroke: "red",
    strokeWidth: 1,
    dash: [20, 2],
    listening: false,
    lineJoin: "bevel",
  });

  static runTask(task: () => void): void {
    if ("requestIdleCallback" in window) {
      // forces the callback to run after 1 second, we don't want the map idling too long after a user selection
      // (initially added due to an infinite AngularJS loop on the venue profile page)
      requestIdleCallback(task, { timeout: 1000 });
    } else {
      setTimeout(task, 1);
    }
  }

  /**
   * Calculates bounds required for focusing a node.
   * Returns the bounds of the x,y and scale for stage reconfiguration.
   */
  calculateZoomPosition(
    node: Konva.Node,
    padding = 0,
    isNodeClick = false
  ): { position: PositionType; scale: number } {
    const segmentBounds = node.getClientRect({
      relativeTo: this.stage,
    });

    /**
     * Calculate "home" scale.
     * Make sure the segments layer fits in the
     * stage layer (only when overrideSegmentFit is not set)
     * + set padding.
     */
    let scale: number;
    const minDimension = Math.min(segmentBounds.width, segmentBounds.height);
    const maxDimension = Math.max(segmentBounds.width, segmentBounds.height);
    const shapeRatio = minDimension / maxDimension;
    const overrideSegmentFit =
      this.seatSize <= ASSIGNED_SEATING.OPTIONS.SMALL_SEAT_SCALE_THRESHOLD ||
      shapeRatio < 0.5;
    // TODO: need better algorithm here - scale should adjust according to
    // seat size instead of using a condition to override this
    if (isNodeClick && overrideSegmentFit) {
      scale = this.getScaleThreshold() + 0.5;
    } else {
      scale = Math.min(
        this.stage.width() / (segmentBounds.width + padding * 2),
        this.stage.height() / (segmentBounds.height + padding * 2),
        this.MAX_SCALE - 0.1
      );
    }

    // set stageCenter object for convience.
    const stageCenter = {
      x: this.stage.width() / 2,
      y: this.stage.height() / 2,
    };

    /**
     * Set segmentBoundsCenter object for convience.
     * Have to normalize with scale, since this isn't done automatically.
     */
    const segmentBoundsCenter = {
      x: (segmentBounds.x + segmentBounds.width / 2) * scale,
      y: (segmentBounds.y + segmentBounds.height / 2) * scale,
    };

    /**
     * Return calculated bounds
     */
    return {
      position: {
        x: -segmentBoundsCenter.x + stageCenter.x,
        y: -segmentBoundsCenter.y + stageCenter.y,
      },
      scale,
    };
  }

  /**
   * Focusing the stage on an x,y position with requested scale.
   */
  focusPosition(
    position: PositionType,
    {
      scale = this.stage.scaleX(),
      animated = false,
    }: { scale: number; animated: boolean },
    forceRevealSection = false
  ): void {
    if (animated) {
      this.stage.to({
        duration: 0.25,
        scaleX: scale,
        scaleY: scale,
        x: position.x,
        y: position.y,
        easing: Konva.Easings.EaseOut,
        onFinish: () => {
          // Defer heavy operations to after animation completes
          requestAnimationFrame(() => {
            this.manageLayers(forceRevealSection);
            this.stage.batchDraw();
          });
        },
      });
    } else {
      this.stage.position(position);
      this.stage.scale({ x: scale, y: scale });
      this.manageLayers(forceRevealSection);
      this.stage.batchDraw();
    }
  }

  // removes highlighted seats that not are not in the seatIds array
  destroyNonSelectedSeats(seatIds: number[]): void {
    const { children } = this.layers.mainLayer;
    for (let i = children.length - 1; i >= 0; i -= 1) {
      const node = children[i];
      if (
        node.attrs.id &&
        !seatIds.includes(Number(node.attrs.id)) &&
        node.attrs.name === "highlight-seat-circle"
      ) {
        node.destroy();
      }
    }
    this.stage.batchDraw();
  }

  destroyExistingSeatsHighlighted(): void {
    // Destroy any existing seating highlights:
    // Iterate backwards through the children array because destroying nodes
    // modifies the array length. Going forwards (starting from the first node) would cause some nodes to be
    const { children } = this.layers.mainLayer;
    for (let i = children.length - 1; i >= 0; i -= 1) {
      const node = children[i];
      if (node.attrs.name === "highlight-seat-circle") {
        node.destroy();
      }
    }
  }

  focusBySeatIds(
    seatsToHighlight: SeatHighlightData,
    {
      scale = this.stage.scaleX(),
      animated = false,
      selectedIndividually = false,
      cb,
      shouldZoom = false,
    }: {
      scale: number;
      animated: boolean;
      selectedIndividually: boolean;
      cb?: () => void;
      shouldZoom?: boolean;
    },
    forceRevealSection = false,
    selectingSeat = true
  ): void {
    // Clear existing highlights and arrays for best available list selections
    if (shouldZoom) {
      this.destroyExistingSeatsHighlighted();
      this.seatingService.clearSelectedSeatIds();
      this.seatingService.clearSeatingObjectArray();
    }

    // Update the last selection type
    if (!shouldZoom) {
      this.lastSelectionWasIndividual = selectedIndividually;
    }

    const {
      segmentIdentifier,
      locationIdentifier,
      permissionsMap,
      pricingMap,
      ticketTypesMap,
      membershipLevelsMap,
      locationData,
      seatIds,
    } = seatsToHighlight;

    seatIds?.forEach((seatId) => {
      // Find the seat within the specific layer

      const seats = this.layers.seatsAvailable.find(
        (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
      );

      const seatOfInterest = seats
        .toArray()
        .find(
          (seat: Konva.Node) =>
            seat.attrs.data.id === seatId &&
            seat.attrs.data.location_identifier === locationIdentifier &&
            seat.attrs.data.segment_identifier === segmentIdentifier
        );

      // For best available selections, check if a highlight already exists for this seat
      if (shouldZoom) {
        const existingHighlight = this.layers.mainLayer.findOne(
          (node: Konva.Node) =>
            node.attrs.name === "highlight-seat-circle" &&
            node.attrs.id === seatId
        );
        if (existingHighlight) {
          return; // Skip creating a duplicate highlight
        }
      }

      const highlightGroup = this.createSeatToHighlight(
        seatOfInterest,
        false,
        cb,
        permissionsMap?.[seatId],
        pricingMap?.[seatId],
        ticketTypesMap?.[seatId],
        membershipLevelsMap?.[seatId],
        locationData
      );

      if (!highlightGroup) {
        return;
      }
      // Add group to the main layer

      // Only zoom and center if shouldZoom is true (for best available selections)
      if (shouldZoom) {
        // Calculate the center position using the seat's local coordinates
        const newPos = {
          x: this.stage.width() / 2 - highlightGroup.x() * scale,
          y: this.stage.height() / 2 - highlightGroup.y() * scale,
        };

        if (animated) {
          this.stage.to({
            duration: 1,
            x: newPos.x,
            y: newPos.y,
            scaleX: scale,
            scaleY: scale,
            easing: Konva.Easings.EaseInOut,
            onFinish: () => {
              this.manageLayers(forceRevealSection);
              this.stage.batchDraw();
            },
          });
        } else {
          this.stage.scale({ x: scale, y: scale });
          this.stage.position(newPos);
          this.manageLayers(forceRevealSection);
          this.stage.batchDraw();
        }
      } else {
        // For manual seat selection, just update layers without zooming
        this.manageLayers(forceRevealSection);
        this.stage.batchDraw();
      }
      this.layers.mainLayer.add(highlightGroup);

      // Only fade out and remove highlight if not selecting a seat
      if (!selectingSeat) {
        highlightGroup.to({
          duration: 1,
          opacity: 0,
          onFinish: () => {
            highlightGroup.destroy();
            this.layers.mainLayer.batchDraw();
          },
        });
      }
    });
  }

  // Experimental; keep for now
  applySkew(
    node: Konva.Node,
    skewX: number,
    skewY: number,
    animated = false,
    duration = 0.5
  ): void {
    // Convert degrees to radians for the transformation
    const skewXRad = (skewX * Math.PI) / 180;
    const skewYRad = (skewY * Math.PI) / 180;

    if (animated) {
      // Animate to the new transformation
      node.to({
        duration,
        skewX: skewXRad,
        skewY: skewYRad,
        easing: Konva.Easings.EaseInOut,
        onFinish: () => {
          this.stage.batchDraw();
        },
      });
    } else {
      // Apply transformation immediately
      node.skewX(skewXRad);
      node.skewY(skewYRad);
      this.stage.batchDraw();
    }
  }

  resetSkew(animated = false, duration = 0.5): void {
    if (animated) {
      this.stage.to({
        duration,
        skewX: 0,
        skewY: 0,
        easing: Konva.Easings.EaseInOut,
        onFinish: () => {
          // this.manageLayers();
          // this.stage.batchDraw();
        },
      });
    } else {
      this.stage.skewX(0);
      this.stage.skewY(0);
      // this.manageLayers();
      // this.stage.batchDraw();
    }
  }

  highlightSegment(seatData: SeatHighlightData, isHovering = false): void {
    this.removeAllSegmentHighlights();

    const segmentGroup = this.findSegmentGroup(
      seatData.segmentIdentifier
    ) as ExtendedKonvaContainer;
    if (!segmentGroup) return;

    if (this.shouldNotDisplayAllSeats()) {
      if (this.activeSegment !== segmentGroup.$segmentData?.id) {
        this.handleDisplaySeats(
          (segmentGroup as ExtendedKonvaContainer).$segmentData,
          isHovering
        );
      }
    }

    this.animateToSegment(segmentGroup, seatData);
  }

  private findSegmentGroup(segmentId: string): Konva.Node | null {
    const segmentsContainer = this.layers.segments.findOne(
      "#segments-container"
    );
    if (!segmentsContainer) return null;

    const children = segmentsContainer.getChildren().toArray();
    return (
      children.find((child) => child.$segmentData?.identifier === segmentId) ||
      null
    );
  }

  private static findAndPrepareSectionShape(
    segmentGroup: Konva.Node
  ): Konva.Node | null {
    const sectionShape = segmentGroup
      .getChildren()
      .toArray()
      .find((child: Konva.Node) => child.attrs?.name === "section");

    if (!sectionShape) return null;

    // Store original stroke properties if not already stored
    if (!sectionShape.getAttr("originalStrokeWidth")) {
      sectionShape.setAttr(
        "originalStrokeWidth",
        sectionShape.getAttr("strokeWidth") || 0
      );
      sectionShape.setAttr(
        "originalStroke",
        sectionShape.getAttr("stroke") || "transparent"
      );
    }

    return sectionShape;
  }

  private animateToSegment(
    segmentGroup: Konva.Node,
    seatData: SeatHighlightData
  ): void {
    const typedSegmentGroup = segmentGroup as ExtendedKonvaGroup;
    // Initially highlight the seat on hover if the segment is not visible; on finish will allow all segments to be revealed.
    if (!this.layers.segments.find("#segments-container")[0].attrs.visible) {
      if (!seatData.locationIdentifier) {
        this.higlightGAOnHover(seatData);
      } else {
        this.higlightSeatOnHover(seatData);
      }
    }
    this.activeSegment = typedSegmentGroup.$segmentData?.id;

    const { scale, position } = this.calculateSegmentTransform(segmentGroup);

    // if we need to move the stage, we need to animate
    const shouldAnimate =
      this.stage.x() !== position.x || this.stage.y() !== position.y;

    this.scaleForHighlight = scale;

    this.stage.to({
      duration: shouldAnimate ? 0.8 : 0,
      x: position.x,
      y: position.y,
      scaleX: scale,
      scaleY: scale,
      easing: Konva.Easings.EaseInOut,
      onFinish: () => {
        this.stage.batchDraw();
        this.manageLayers(true);
        if (this.layers.segments.find("#segments-container")[0].attrs.visible) {
          if (!seatData.locationIdentifier) {
            this.higlightGAOnHover(seatData);
          } else {
            this.higlightSeatOnHover(seatData);
          }
        }
      },
    });
  }

  private createSeatToHighlight(
    seatOfInterest?: Konva.Node,
    isHovering = false,
    cb?: (seatingObject?: SeatWithLocationDataType) => Promise<void> | void,
    seatPermissionId?: number,
    price?: string,
    ticketTypeId?: number,
    membershipLevelId?: number,
    locationData?: PublicBaseLocationSerializer
  ): Konva.Group | null {
    const seatNode: Konva.Container = this.layers.seatsAvailable.findOne(
      `#${SeatFactory.createSeatId(seatOfInterest?.attrs.data.id)}`
    );

    if (!seatNode) {
      return null;
    }
    // add to seating object array
    const seatingObject = {
      ...seatOfInterest?.attrs.data,
      permissionId: seatPermissionId,
      ticketTypeId,
      membershipLevelId,
      price,
      locationData,
    };

    // Track the seat ID if it's not already in our selection & don't add seat to selection if hovering
    const numericSeatId = seatOfInterest?.attrs?.data?.id
      ? Number(seatOfInterest.attrs.data.id)
      : null;

    if (numericSeatId && !isHovering) {
      if (
        !this.seatingService.seatingObjectArray.some(
          (seat) => seat.id === numericSeatId
        )
      ) {
        this.seatingService.addSeatToSeatingObjectArray(seatingObject);
      }
    }

    // Only skip duplicate highlights for pure individual seat selections
    // Allow isolated seat removal and best available selections to create highlights
    if (cb === undefined && !isHovering && numericSeatId) {
      const existingHighlight = this.layers.mainLayer.findOne(
        (node: Konva.Node) =>
          node.attrs.name === "highlight-seat-circle" &&
          node.attrs.id === numericSeatId
      );
      if (existingHighlight) {
        return null; // Skip creating a duplicate highlight only for individual selections
      }
    }

    const absolutePos = seatNode.getAbsolutePosition();
    const seatPositionRelativeToStagePos = {
      x: (absolutePos.x - this.stage.x()) / this.stage.scaleX(),
      y: (absolutePos.y - this.stage.y()) / this.stage.scaleY(),
    };

    // Create a group to hold circle
    const highlightGroup = new Konva.Group({
      x: seatPositionRelativeToStagePos.x,
      y: seatPositionRelativeToStagePos.y,
      name: isHovering
        ? "highlight-seat-circle-hover"
        : "highlight-seat-circle",
      transformsEnabled: "all",
      isActive: true,
      id: seatOfInterest?.attrs.data.id,
    });

    // Create the highlight circle
    const circle = new Konva.Circle({
      radius: this.seatSize,
      isActive: true,
      ...StageService.getSeatHighlightStyle(isHovering),
    });

    // Add click event handler if not hovering; to remove seat selection
    if (!isHovering && cb) {
      circle.on("click tap", async () => {
        if (cb) {
          await cb(seatingObject);
          this.destroyNonSelectedSeats(
            this.seatingService.seatingObjectArray.map((seat) => seat.id)
          );
        } else {
          this.removeSeatSelection(Number(seatOfInterest?.attrs.data.id));
        }
      });
    }

    // Add circle to the group
    highlightGroup.add(circle);

    return highlightGroup;
  }

  removeGaSelection(id: number): void {
    const gaGroup = this.layers.mainLayer.findOne(
      (node: Konva.Node) =>
        node.nodeType === "Group" &&
        (node as Konva.Container).findOne(
          (child: Konva.Node) =>
            child.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
            child.attrs.id === id
        )
    );

    if (gaGroup) {
      gaGroup.destroy();
      this.removeSeatFromBestAvailable(id);
      this.stage.batchDraw();
    }
  }

  removeAllBestAvailableGaSelections(): void {
    // This is primarily for Call backs of GA's; in other words if they click on the GA Location again
    // during BA, we run this to avoid too many GA nodes layering on top of each other
    const gaGroups = this.layers.mainLayer.find(
      (node: Konva.Node) =>
        node.nodeType === "Group" &&
        (node as Konva.Container).findOne(
          (child: Konva.Node) =>
            child.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
            child.attrs.isBestAvailableSelected
        )
    );

    const gaGroupIds = gaGroups
      .toArray()
      .map(
        (group: Konva.Node) =>
          (group as Konva.Container).findOne(
            (child: Konva.Node) =>
              child.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID
          ).attrs.id
      );

    if (gaGroups) {
      gaGroups.toArray().forEach((group) => {
        group.destroy();
      });
      gaGroupIds.forEach((id) => {
        this.removeSeatFromBestAvailable(id);
      });
      this.stage.batchDraw();
    }
  }

  removeSeatFromBestAvailable(id: number | string): void {
    this.seatingService.removeSeatFromSelectedSeatIds(Number(id));
    this.seatingService.removeSeatFromSeatingObjectArray(Number(id));
    // Also remove the visual highlight
    this.removeSeatSelection(Number(id));
  }

  removeSeatSelection(seatId: number): void {
    const node = this.layers.mainLayer.findOne(
      (node: Konva.Node) => Number(node.attrs.id) === seatId
    );
    if (node) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - setLocationSelected only requires id to remove a seat, but the type expects more fields
      this.setLocationSelected({ seat: { id: seatId } }, false);
      node.destroy();
      this.layers.mainLayer.batchDraw();
      // Update the selected seats arrays
      this.removeSeatFromBestAvailable(seatId);
    }
  }

  private static getSeatHighlightStyle(isHovering: boolean): {
    fill: string;
    stroke: string;
    strokeWidth: number;
    opacity: number;
  } {
    const styleKey = isHovering
      ? "BEST_AVAILABLE_HOVERED"
      : "BEST_AVAILABLE_CHOSEN";
    const style = ASSIGNED_SEATING.SEAT.STYLE[styleKey];

    return {
      fill: style.colorFill,
      stroke: style.colorStroke,
      strokeWidth: style.strokeWidth,
      opacity: style.opacity,
    };
  }

  destroyExistingHoverHighlights(): void {
    this.destroyExistingGASelections();
    // Destroy any existing hover highlights:
    // Iterate backwards through the children array because destroying nodes
    // modifies the array length.
    const { children } = this.layers.mainLayer;
    for (let i = children.length - 1; i >= 0; i -= 1) {
      const node = children[i];
      if (node.attrs.name === "highlight-seat-circle-hover") {
        node.destroy();
      }
    }
  }

  destroyExistingGASelections(): void {
    this.gaLocationsHovered.forEach((id) => {
      const gaGroup = this.layers.mainLayer.findOne(
        (node: Konva.Node) =>
          node.nodeType === "Group" &&
          (node as Konva.Container).findOne(
            (child: Konva.Node) =>
              child.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
              child.attrs.data.id === id &&
              child.attrs.label === `GA ${id.toString()}`
          )
      );
      gaGroup?.destroy();
    });

    this.gaLocationsHovered = [];
    this.stage.batchDraw();
  }

  private higlightSeatOnHover(seatsToHighlight: SeatHighlightData): void {
    this.destroyExistingHoverHighlights();
    const { seatIdentifiers, segmentIdentifier, locationIdentifier } =
      seatsToHighlight;
    seatIdentifiers.forEach((seatId) => {
      // Find the seat within the specific layer

      const seats = this.layers.seatsAvailable.find(
        (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
      );

      const seatOfInterest = seats
        .toArray()
        .find(
          (seat: Konva.Node) =>
            seat.attrs.data.identifier === seatId &&
            seat.attrs.data.location_identifier === locationIdentifier &&
            seat.attrs.data.segment_identifier === segmentIdentifier
        );

      const highlightGroup = this.createSeatToHighlight(seatOfInterest, true);
      if (!highlightGroup) {
        return;
      }
      this.layers.mainLayer.add(highlightGroup);
      this.layers.mainLayer.batchDraw();
    });
  }

  private higlightGAOnHover(locationToHighlight: SeatHighlightData): void {
    this.destroyExistingGASelections();
    const gaNode = this.layers.seatsAvailable.findOne(
      (node: Konva.Node) =>
        node.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
        node.attrs.data.id === locationToHighlight.id
    );

    if (gaNode) {
      if (locationToHighlight.id) {
        this.gaLocationsHovered.push(locationToHighlight.id);
      }

      const { gaNodeClone, gaGroup } = this.createGANodeClone(
        gaNode,
        ASSIGNED_SEATING.SEAT.STYLE.BEST_AVAILABLE_HOVERED
      );

      gaNodeClone.setAttr("label", `GA ${locationToHighlight.id}`);
      const absolutePos = gaNode.getAbsolutePosition();
      const gaPositionRelativeToStage = {
        x: (absolutePos.x - this.stage.x()) / this.stage.scaleX(),
        y: (absolutePos.y - this.stage.y()) / this.stage.scaleY(),
      };
      gaNodeClone.setAttr("x", gaPositionRelativeToStage.x);
      gaNodeClone.setAttr("y", gaPositionRelativeToStage.y);
      this.layers.mainLayer.add(gaGroup);
    }
    this.stage.batchDraw();
  }

  private calculateSegmentTransform(segmentGroup: Konva.Node): {
    scale: number;
    position: { x: number; y: number };
  } {
    const segmentBounds = segmentGroup.getClientRect({
      relativeTo: this.stage,
    });

    const scale =
      Math.min(
        this.stage.width() / (segmentBounds.width * 1.2),
        this.stage.height() / (segmentBounds.height * 1.2),
        this.MAX_SCALE - 0.1
      ) / 1.5; // Divide by 1.5 to match original scaling

    const stageCenter = {
      x: this.stage.width() / 2,
      y: this.stage.height() / 2,
    };

    const segmentCenter = {
      x: segmentBounds.x + segmentBounds.width / 2,
      y: segmentBounds.y + segmentBounds.height / 2,
    };

    return {
      scale,
      position: {
        x: stageCenter.x - segmentCenter.x * scale,
        y: stageCenter.y - segmentCenter.y * scale,
      },
    };
  }

  removeAllSegmentHighlights(): void {
    const segmentsContainer = this.layers.segments.findOne(
      "#segments-container"
    );
    if (!segmentsContainer) return;

    segmentsContainer
      .getChildren()
      .toArray()
      .forEach((segmentGroup) => {
        const sectionShape = segmentGroup
          .getChildren()
          .toArray()
          .find((child: Konva.Node) => child.attrs?.name === "section");

        if (sectionShape) {
          // Restore original stroke properties if they exist
          const originalStrokeWidth = sectionShape.getAttr(
            "originalStrokeWidth"
          );
          const originalStroke = sectionShape.getAttr("originalStroke");

          if (originalStrokeWidth !== undefined) {
            sectionShape.setAttr("strokeWidth", originalStrokeWidth);
            sectionShape.setAttr("stroke", originalStroke);
          }
        }
      });

    this.layers.segments.batchDraw();
  }

  /**
   * Run this function when not all seats are displayed to the user.
   *
   * Updates the map so that seats in the pending cart array are shown as selected.
   * Also updates the map to show the correct seats as enabled/disabled when selecting minimum purchase seats.
   */
  updatePendingSeatDisplayOnMap() {
    // ensures the user's selected seat(s) keep their checkmark styling when clicking into different sections
    this.seatingService.pendingCartPermissions.forEach((s) => {
      this.setLocationSelected({ seat: s.$data }, true);
    });

    // don't hide item types when multiple item types can be added to the pending cart
    if (this.seatingService.overridePurchaseLimits?.multiItemsAllowed) {
      return;
    }

    if (this.seatingService.pendingCartPermissions[0].itemTypeId) {
      // enables seats with the item type currently in the pending cart
      // disables seats without the pending item type
      this.enableSeatsByItemType([
        this.seatingService.pendingCartPermissions[0].itemTypeId,
      ]);
      // disables GA locations while there are seats in the pending cart
      this.enableGAByItemType(false, [
        this.seatingService.pendingCartPermissions[0].itemTypeId,
      ]);
    }
  }

  /**
   * Centers stage to show entire assigned seating map
   */
  centerStage(): void {
    // layer segments must be shown to get defined segmentBounds values
    // in the calculateZoomPosition function
    this.layers.segments.findOne("#segments-container").show();

    // homebounds must be recalculated every time the browser is resized
    const homeBounds = this.calculateZoomPosition(this.layers.segments, 200);

    this.focusPosition(homeBounds.position, {
      scale: homeBounds.scale,
      animated: false,
    });
  }

  seatIsInViewport(el: Konva.Node): boolean {
    const stageBounds = {
      x: 0,
      y: 0,
      width: this.stage.width(),
      height: this.stage.height(),
    };
    const absPosition = el.absolutePosition();
    const elWidth = el.width() * this.stage.scale().x;
    const elHeight = el.height() * this.stage.scale().y;

    const elBounds = {
      x: absPosition.x - elWidth / 2,
      y: absPosition.y - elHeight / 2,
      width: elWidth,
      height: elHeight,
    };
    return StageService.isInViewPort(stageBounds, elBounds, 0);
  }

  /**
   * Sets which seats are currently in the viewport
   * Allows us to perform actions only on seats that the user
   * can see; for better performance
   */
  setSeatsInViewport(): void {
    // Debounce this so that we perform this only when the viewport is last updated
    if (this.seatsInViewportTimer !== null) {
      clearTimeout(this.seatsInViewportTimer);
    }
    this.seatsInViewportTimer = setTimeout(() => {
      // function updates all row elements' inviewport attribute
      const updateRows = (el) => {
        const seatNodes = el.find(
          (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
        );
        // use for() loop to iterate over seats
        for (let i = 0; i < seatNodes.length; i += 1) {
          const seat = seatNodes[i];
          seat.setAttr("inViewPort", this.seatIsInViewport(seat));
        }
      };
      this.layers.seatsDisabled.getChildren().each(updateRows);
      this.layers.seatsAvailable.getChildren().each(updateRows);
    }, 250);
  }

  /**
   * Adjust pixel ratio by seat size
   * Smaller seats need more pixels to see
   * them clearer
   */
  getRatioBySeatSize(): number {
    let returnSize = 2;
    if (this.seatSize < 5) {
      returnSize = 3;
    } else if (this.seatSize < 2) {
      returnSize = 5;
    }
    return returnSize;
  }

  getPixelRatio(): number {
    return Math.min(
      // get max between stage scale and default pixelRatio
      Math.max(this.stage.scale().x * 2, 1),
      this.getRatioBySeatSize() // maximum pixel ratio
    );
  }

  static isInViewPort(
    viewBounds: BoundsType,
    elBounds: BoundsType,
    padding = 0
  ): boolean {
    if (
      elBounds.x + elBounds.width > viewBounds.x - padding &&
      elBounds.y + elBounds.height > viewBounds.y - padding &&
      elBounds.x < viewBounds.width + padding &&
      elBounds.y < viewBounds.height + padding
    ) {
      return true;
    }
    return false;
  }

  cullView(): void {
    const padding = 200;
    const stageBounds = {
      x: 0,
      y: 0,
      width: this.stage.width(),
      height: this.stage.height(),
    };

    const toggleElement = (el: Konva.Node): void => {
      /**
       * When moving/dragging around the canvas, the stage is
       * actually the element that moves, NOT the children.
       * Because of this we can use the absolutePosition method to get
       * the relative x,y values.
       * el.width and el.height are NOT relative, so we have to scale them
       * accordingly to get the proper values.
       *
       * x & y values for absolutePosition will be at the center of the group,
       * since it does not actually have bounds. Height and width are attained from the children.
       * So we have to subtract half the width & height from x / y respectively to get the top left
       * x & y which is easier to work with.
       */
      const stageScaleX = this.stage.scale().x;
      const stageScaleY = this.stage.scale().y;
      const absPosition = el.absolutePosition();
      const elWidth = el.width() * this.stage.scale().x;
      const elHeight = el.height() * this.stage.scale().y;
      const offset = el.getAttr("visibleGroupOffset");

      const elBounds = {
        x: absPosition.x - elWidth / 2 + offset.x * stageScaleX,
        y: absPosition.y - elHeight / 2 + offset.y * stageScaleY,
        width: elWidth,
        height: elHeight,
      };
      const inView = StageService.isInViewPort(stageBounds, elBounds, padding);
      const cachedRatio = el.getAttr("cachedRatio");

      if (inView) {
        el.show();
        // get min between max pixel res and 2x stage scale.
        // 2x for best quality res
        const pixelRatio = this.getPixelRatio();
        if (!cachedRatio || cachedRatio < pixelRatio) {
          el.cache({ pixelRatio });
          el.setAttr("cachedRatio", pixelRatio);
        }
      } else if (!cachedRatio) {
        el.hide();
      }
    };

    this.layers.seatsDisabled.getChildren().each(toggleElement);
    this.layers.seatsAvailable.getChildren().each(toggleElement);

    this.setSeatsInViewport();

    this.layers.seatsDisabled.batchDraw();
    this.layers.seatsAvailable.batchDraw();
  }

  handleDrag = (): void => {
    throttle(this.cullView, 250)();
    this.handleMouseMove({ hidden: true });
  };

  /**
   * @returns the proper scale depending on seat size
   */
  getScaleThreshold(): number {
    return this.seatSize <= ASSIGNED_SEATING.OPTIONS.SMALL_SEAT_SCALE_THRESHOLD
      ? 1.5
      : 0.5;
  }

  shouldHighlightSection(forceRevealSection = false): boolean {
    // Define conditions that prevent section highlighting
    const conditions = [
      this.displayAllSeats, // All seats are being displayed
      forceRevealSection, // Section reveal is being forced
      this.layers.alwaysDisplaySeatsAndLocations, // Always show seats/locations setting is enabled
      !this.layers.segments.find("#segments-container")[0].attrs.visible, // Are sections being shown
      this.layers.segments.find("#segments-container")[0].children.length === 1, // Only one section exists
      this.isForPrint, // Map is being printed,
      Boolean(this.activeSegment),
    ];

    return !conditions.some((condition) => condition);
  }

  manageLayers(forceRevealSection = false): void {
    if (this.manageLayersTimer !== null) {
      clearTimeout(this.manageLayersTimer);
    }
    /**
     * Show seats/locations if scale is over 0.5 or only 1 section
     * or if set by the user on the space
     * or map printing is requested
     */
    if (
      this.displayAllSeats ||
      forceRevealSection ||
      this.layers.alwaysDisplaySeatsAndLocations ||
      this.stage.scaleX() > this.getScaleThreshold() ||
      this.layers.segments.find("#segments-container")[0].children.length ===
        1 ||
      this.isForPrint
    ) {
      this.manageLayersTimer = setTimeout(() => {
        // If we force not displaying all seats then only hide the segment
        // container for the user's selected segment
        if (this.shouldNotDisplayAllSeats() && this.activeSegment) {
          const segmentNodes = this.layers.segments.findOne(
            "#segments-container"
          ).children;
          segmentNodes.each((node) => {
            if (node.$segmentData.id === this.activeSegment) {
              node.hide();
            }
          });
        } else if (!this.shouldNotDisplayAllSeats()) {
          // hide segment container for all segments to reveal all seats
          this.layers.segments.find("#segments-container").each((segment) => {
            segment.hide();
          });
        }

        this.layers.seatsDisabled.show();
        this.layers.seatsAvailable.show();
        this.cullView();

        this.stage.batchDraw();
      }, 50);

      this.stage.on("dragmove", throttle(this.handleDrag, 250));
    } else {
      /**
       * Show clickable segments if zoom < 0.5 or more than 2 sections
       */
      if (this.shouldNotDisplayAllSeats() && this.activeSegment) {
        // If we force not displaying all seats then reveal the segment
        // container for the user's selected segment
        const segmentNodes = this.layers.segments.findOne(
          "#segments-container"
        ).children;
        segmentNodes.each((node) => {
          if (node.$segmentData.id === this.activeSegment) {
            node.show();
          }
        });
      } else {
        this.layers.segments.find("#segments-container").each((segment) => {
          segment.show();
        });
      }
      this.layers.seatsDisabled.hide();
      this.layers.seatsAvailable.hide();
      this.stage.off("dragmove");
    }
  }

  handleScroll = (e: WheelEvent): void => {
    e.preventDefault();

    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
    }

    const oldScale = this.stage.scaleX();
    const mousePointTo = this.getScaledPointerPosition();
    const newScale = e.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;

    if (newScale >= this.MAX_SCALE || newScale <= this.MIN_SCALE) {
      this.scrollTimer = setTimeout(() => {
        this.cullView();
      }, 250);
      return;
    }

    // set a default value if this returns undefined
    const newMousePointTo = this.stage.getPointerPosition() ?? { x: 0, y: 0 };
    const newPos = {
      x: -(mousePointTo.x - newMousePointTo.x / newScale) * newScale,
      y: -(mousePointTo.y - newMousePointTo.y / newScale) * newScale,
    };

    this.stage.scale({ x: newScale, y: newScale });
    this.stage.position(newPos);
    this.stage.batchDraw();

    this.manageLayers();
  };

  addBackgroundImage(src: string, { width, height, x, y }: BoundsType): void {
    const image = new Image();
    image.onload = () => {
      const bgImage = new Konva.Image({
        x: 0,
        y: 0,
        image,
        listening: false,
        width: width || 100,
        height: height || 100,
        id: "bg-image",
      });
      bgImage.x(x);
      bgImage.y(y);

      this.layers.background.add(bgImage);
      bgImage.moveToBottom();

      this.layers.background.draw();
      (this.stage as ExtendedKonvaStage).backgroundImageLoaded = true;
    };

    // Add a changing query string to the image src to prevent the browser from using the cached image
    // otherwise some browsers will attempt to use the wrong cache due to us setting `crossOrigin="Anonymous"`
    // Mainly an issue with how the behaviour of S3 interacts with Chromium
    // more information here: https://stackoverflow.com/a/44866772
    // and here https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
    // information regarding Vary: Origin header:
    // https://medium.com/@anonrongbo/understanding-the-importance-of-vary-origin-to-prevent-cache-confusion-and-cors-errors-ef3b63046b00
    const queryString = new Date().getTime();
    image.src = `${src}?${queryString}`;
    image.crossOrigin = "Anonymous";
  }

  initSegments(segments: PublicBaseSegmentSetSerializer[]): void {
    const segmentGroup = new Konva.Group({
      id: "segments-container",
    });
    segments.forEach((segmentData) => {
      const segment = this.segmentFactory.create(segmentData);
      // attach segment object from db to node
      (segment.node as ExtendedKonvaGroup).$segmentData = segmentData;

      // If segment is the stage or screen, don't add to groups
      // Groups will be hidden, and we want the stage to remain constant
      if (
        optionsAsList(ASSIGNED_SEATING.FOCAL_SECTIONS).some(
          (section) => section.ID === segment.node.id()
        )
      ) {
        this.layers.segments.add(segment.node);
      } else {
        segmentGroup.add(segment.node);
      }
    });

    this.layers.segments.add(segmentGroup);
    segmentGroup.show();
    this.layers.segments.moveToTop();
    this.layers.segments.draw();
  }

  /**
   * When using the ticket assigner, handle the deselection of GA locations
   * Pass in a currentId to deselect everything but the current selection
   * Pass in no currentId to deselect every location
   */
  deselectLocations = (nodeArray, currentId = null) => {
    for (let i = 0; i < nodeArray.length; i += 1) {
      const loc = nodeArray[i];
      // do not deselect the current selection
      if (currentId && currentId === loc.attrs.data.id) {
        return;
      }
      const display = loc.findAncestor(".display");
      const location = display.findAncestor(".location");
      location.clearCache();
      const label = display.findOne(".label");
      loc.setAttr("stroke", label.getAttr("fill"));
      loc.setAttr("selected", false);
    }

    // redraw stage
    this.stage.batchDraw();
  };

  /**
   * When using the ticket assigner, handle seat deselection
   * Pass in a currentId to deselect everything but the current selection
   * Pass in no currentId to deselect every seat
   */
  deselectSeats = (nodeArray, currentId = null) => {
    for (let i = 0; i < nodeArray.length; i += 1) {
      const seat = nodeArray[i];
      // do not deselect the current selection
      if (currentId && currentId === seat.attrs.data.id) {
        return;
      }
      const display = seat.findAncestor(".display");
      const location = display.findAncestor(".location");
      location.clearCache();
      const highlight = display.findOne(".highlight");
      highlight.opacity(0);
      seat.setAttr("selected", false);
    }

    // redraw stage
    this.stage.batchDraw();
  };

  /**
   * Handle click event function.
   * Runs the proper click event in the task manager.
   */
  handleClick(node: Konva.Node): void {
    // if area and not attendance mode, handle general admission click
    // attendance mode currently does not support ga areas
    if (
      node.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
      this.seatingService.mode !== ASSIGNED_SEATING_MODES.ATTENDANCE.id // TODO: support locations with attendance mode
    ) {
      if (node.attrs.isBestAvailableSelected === true) {
        StageService.runTask(() =>
          this.generalAdmissionFactory.handleBestAvailableGAClick(node)
        );
        return;
      }
      StageService.runTask(() =>
        this.generalAdmissionFactory.handleClick(
          node,
          this.layers.seatsAvailable
        )
      );
    }

    if (node.name() === ASSIGNED_SEATING.SEAT.ID) {
      StageService.runTask(() =>
        this.seatFactory.handleSeatClick(
          node,
          this.layers.seatsAvailable,
          this.getPixelRatio()
        )
      );
    }

    if (node.name() === ASSIGNED_SEATING.SEGMENT.ID) {
      const position = this.calculateZoomPosition(node, 0, true);
      // always display contents when a segment is clicked
      const forceRevealSection = true;
      // clear previous selections when a new segment is clicked
      this.stage.clearSelections();
      this.focusPosition(
        position.position,
        {
          scale: position.scale,
          animated: true,
        },
        forceRevealSection
      );
      if (this.shouldNotDisplayAllSeats()) {
        this.handleDisplaySeats(
          (node.getParent() as ExtendedKonvaContainer).$segmentData
        );
      }
    }
  }

  /**
   *  Removes the previous segment highlight when hovering over another segment or the map container
   */
  removeSegmentHighlight(): void {
    if (this.hoveredSegment) {
      this.hoveredSegment.setAttr(
        "fill",
        ASSIGNED_SEATING.SEGMENT.STYLE.AVAILABLE.colorFill
      );
      this.hoveredSegment = null;
    }
  }

  /**
   *  Enables seats on the map with the same resale id as the hovered seat.
   *  Disables all other seats.
   */
  handleResaleDisplay(node: Konva.Node): void {
    // check if the input node has a resale id
    const seatData = this.seatingService.seatingMap[node?.attrs?.data?.id];
    const resaleId = seatData?.find(
      (seat) =>
        seat.seatingItemResaleId &&
        seat.seatingItemResaleStatus ===
          enums.RESALE_SUBMISSION_STATUS_MAP.PENDING.id
    )?.seatingItemResaleId;
    if (resaleId) {
      // enable seats in the seatIdArray, disable all other seats
      const seatIdArray = this.seatingService.resaleMap[resaleId];
      this.enableSeatsById(seatIdArray);
    }
  }

  /**
   * Handle hover events.
   */
  handleHover({ evt }: { evt: MouseEvent }, node: Konva.Node): void {
    const name = node.name();
    if (name === ASSIGNED_SEATING.SEAT.ID) {
      // enable/disable seats if they have the same resale id or not
      this.handleResaleDisplay(node);
      // handle tooltip data / visibility
      this.handleMouseMove({
        data: { ...node?.attrs?.data, name },
        mouseX: evt.offsetX,
        mouseY: evt.offsetY,
        hidden: false,
      });
    } else if (name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
      const segment_identifier =
        node.findAncestor(".location")?.attrs?.identifier;
      // handle tooltip data / visibility
      this.handleMouseMove({
        data: { ...node.attrs.data, name, segment_identifier },
        mouseX: evt.offsetX,
        mouseY: evt.offsetY,
        hidden: false,
      });
    } else if (name === ASSIGNED_SEATING.SEGMENT.ID) {
      // remove previous segment highlight when hovering over another segment
      this.removeSegmentHighlight();
      // add highlight to current hovered segment
      node.setAttr("fill", ASSIGNED_SEATING.SEGMENT.STYLE.AVAILABLE.hoverFill);
      this.hoveredSegment = node;
      this.stage.batchDraw();
    }
    // set pointer
    this.stage.container().style.cursor = "pointer";
  }

  async handleDisplaySeats(
    segmentData: PublicBaseSegmentSetSerializer,
    isHovering = false
  ): Promise<void> {
    // If another segment was focused on prior to this, reveal the segment container for it
    if (this.activeSegment) {
      const segmentNodes = this.layers.segments.findOne(
        "#segments-container"
      ).children;
      segmentNodes.each((node: ExtendedKonvaGroup) => {
        if (node.$segmentData.id === this.activeSegment) {
          node.show();
        }
      });
    }
    // save chosen segment to instance
    this.setActiveSegment(segmentData.id, !isHovering);
    this.layers.seatsAvailable.destroyChildren();
    this.layers.seatsDisabled.destroyChildren();
    await this.getAndDrawLocations({ segmentData, getSingleLocation: true });

    // if there are pending cart items and not all seats are displayed on the map,
    // update each section's display as it is clicked into
    if (this.seatingService.pendingCartPermissionsCount > 0) {
      this.updatePendingSeatDisplayOnMap();
    }
  }

  /**
   * Cache each seat in seat layers for performance
   */
  cacheSeats(): void {
    this.layers.seatsDisabled.show();
    this.layers.seatsAvailable.show();

    const cacheElement = (el: Konva.Node) => {
      const pixelRatio = this.getPixelRatio() || 1;
      el.cache({
        pixelRatio,
      });
      el.setAttr("cachedRatio", pixelRatio);
    };
    this.layers.seatsDisabled.getChildren().each(cacheElement);
    this.layers.seatsAvailable.getChildren().each(cacheElement);
  }

  /**
   * Retrieve locations and draw them on the map
   */
  async getAndDrawLocations({
    segmentData,
    getSingleLocation = false,
  }: {
    segmentData: PublicBaseSegmentSetSerializer;
    getSingleLocation?: boolean;
  }): Promise<void> {
    const createLocations = (
      segmentData: PublicBaseSegmentSetSerializer,
      locationData: PublicBaseLocationSerializer[]
    ) => {
      const locationGroups = this.locationFactory.create(
        locationData,
        segmentData.display,
        segmentData.identifier,
        segmentData.transform,
        this.seatSize,
        this.seatSpacing,
        segmentData.hide_location_names,
        this.hideSegmentBackground
      );

      if (locationGroups.locationGroupActive.hasChildren()) {
        this.layers.seatsAvailable.add(locationGroups.locationGroupActive);
      }

      this.layers.seatsDisabled.add(locationGroups.locationGroupDisabled);
    };

    if (
      !optionsAsList(ASSIGNED_SEATING.FOCAL_SECTIONS).some(
        (section) => section.ID === segmentData.identifier
      )
    ) {
      const promise = this.seatingService
        .fetchSingleLocationData(segmentData.id)
        .then((locationData) => {
          createLocations(segmentData, locationData);
          if (getSingleLocation) {
            this.cacheSeats();
            this.stage.batchDraw();
          }
        });

      await Promise.resolve(promise);
    }
  }

  async initSeating(
    segments: PublicBaseSegmentSetSerializer[],
    seatSize: string,
    seatSpacing: number
  ): Promise<void> {
    this.seatSize = Number(seatSize);
    // Allow zooming in more if seat size is configured smaller
    if (this.seatSize < 2) {
      this.MAX_SCALE = 7;
    } else if (
      this.seatSize <= ASSIGNED_SEATING.OPTIONS.SMALL_SEAT_SCALE_THRESHOLD
    ) {
      this.MAX_SCALE = 5;
    }
    this.seatSpacing = seatSpacing;

    // If correct conditions are met
    // (e.g.: map is small enough or we should force displaying
    // locations selectively), draw out all the locations
    // and add them to the cache
    if (!this.shouldNotDisplayAllSeats()) {
      const promiseArray = segments.map(async (segmentData) => {
        await this.getAndDrawLocations({ segmentData });
      });

      await Promise.all(promiseArray);

      this.cacheSeats();
    }

    this.layers.seatsDisabled.hide();
    this.layers.seatsAvailable.hide();
  }

  async init(
    elementId: string,
    space: PublicBaseSpaceSerializer,
    {
      width,
      height,
      handleComplete,
    }: {
      width: number;
      height: number;
      handleComplete: (input?: unknown) => unknown;
    }
  ): Promise<void> {
    const stage = new Konva.Stage({
      x: 0,
      y: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      container: elementId,
      width,
      height,
      draggable: true,
      name: "stage",
      preventDefault: true,
    }) as ExtendedKonvaStage;

    const layers = {
      background: new Konva.Layer({ name: "background" }),
      seatsAvailable: new Konva.Layer({ name: "seatsAvailable" }),
      seatsDisabled: new Konva.Layer({
        name: "seatsDisabled",
        listening: false,
      }),
      segments: new Konva.Layer({ name: "segments" }),
      alwaysDisplaySeatsAndLocations: space.always_display_seats_and_locations,
      mainLayer: new Konva.Layer({ name: "mainLayer" }),
    };

    this.space = space;
    this.stage = stage;
    this.layers = layers;
    this.elementId = elementId;
    this.displayAllSeats = false;

    // reset this when reloading the map, otherwise the active segment disappears
    this.setActiveSegment(null);

    this.hideSegmentBackground = Boolean(space.hide_sections_on_map);

    this.layers.mainLayer.add(this.selectionRect);

    this.stage.add(this.layers.background);
    this.stage.add(this.layers.segments);
    this.stage.add(this.layers.seatsDisabled);
    this.stage.add(this.layers.seatsAvailable);
    this.stage.add(this.layers.mainLayer);

    if (space.background_file && space.background_transform) {
      this.addBackgroundImage(
        space.background_file,
        space.background_transform
      );
    }

    if (space.segment_set) {
      this.initSegments(space.segment_set);
      this.initSeating(
        space.segment_set,
        space.seat_size,
        space.seat_spacing
      ).then(() => {
        if (handleComplete) {
          handleComplete();
        }
      });
    }

    stage.on(
      "click tap mousemove mouseleave",
      (e: Konva.KonvaEventObject<MouseEvent> & { type: string }) => {
        this.isPinchingGesture = false;
        const node: Konva.Node = e.target;
        const eventType: string = e.type;
        const mouseEvent: MouseEvent = e.evt;

        // only fire events if data is attached, this means you can interact with a seat
        if (node && node.attrs.isActive) {
          if (eventType === "mousemove") {
            this.handleHover(e, node);
          }
          if (
            (eventType === "click" || eventType === "tap") &&
            !this.isPinchingGesture
          ) {
            // hides tooltip
            this.handleMouseMove({ hidden: true });
            this.handleClick(node);
          }
        } else {
          const name = node.name();

          // If node is a segment and the segment is a sold_out_segment,
          // display a tooltip. This is similar logic to `handleHover` but we bypass the hover logic
          // and directly display the tooltip instead.
          if (
            name === ASSIGNED_SEATING.SEGMENT.ID &&
            this.seatingService.soldOutSegments.includes(node.attrs.data.id)
          ) {
            this.handleMouseMove({
              data: { ...node.attrs.data, name },
              mouseX: mouseEvent.offsetX,
              mouseY: mouseEvent.offsetY,
              hidden: false,
            });
          } else {
            // If not a segment, hide the tooltip.
            // We need to explicitly put this in an else statement after a segment node check
            // as the handleHover function will call handleMouseMove
            this.handleMouseMove({ hidden: true });
          }

          // if no data exists, set cursor to default
          this.stage.container().style.cursor = "default";
          // remove segment highlight when not hovering over any elements
          this.removeSegmentHighlight();

          // if the mouse was previously hovered over a resale seat,
          // refresh the display for resale seats when not hovering over any elements
          if (this.isSeatFilterEnabled) {
            this.enableSeatsById([]);
          }

          this.stage.batchDraw();
          if (
            (eventType === "click" || eventType === "tap") &&
            !this.isPinchingGesture
          ) {
            // when clicking + dragging,
            // do not deselect a multi-selection when releasing the mouse
            if (this.isItemAssigner()) {
              const seatNodes = this.layers.seatsAvailable.find(
                (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
              );
              // use for() loop to iterate over seats
              for (let i = 0; i < seatNodes.length; i += 1) {
                const seat = seatNodes[i];
                const display = seat.findAncestor(".display");
                const location = display.findAncestor(".location");
                location.clearCache();
              }
              if (this.isMultiSelection) {
                this.isMultiSelection = false;
                return;
              }
            }

            // Only clear selections if we're not in best available mode or if there are no selected seats
            // This prevents accidental clearing when clicking empty areas during seat selection
            if (this.seatingService.seatingObjectArray.length === 0) {
              (stage as ExtendedKonvaStage).clearSelections();
            }
          }
        }
      }
    );

    (stage as ExtendedKonvaStage).clearSelections = () => {
      // Clear selected seats
      const seatNodes = this.layers.seatsAvailable.find(
        (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
      );
      // use for() loop to iterate over seats
      for (let i = 0; i < seatNodes.length; i += 1) {
        const seat = seatNodes[i];
        const display = seat.findAncestor(".display");
        const location = display.findAncestor(".location");
        location.clearCache();
        const highlight = (display as Konva.Container).findOne(".highlight");
        highlight.opacity(0);

        // clear selections ONLY on the assigner map, we want to remove highlighted seats when moving between segments
        // keep selections on the sales map, we want seats in the basket to keep their selected values
        if (this.isItemAssigner()) {
          seat.setAttr("selected", false);
        }
      }

      // Clear the selected seat IDs and notify listeners
      if (this.seatingService.seatingObjectArray.length > 0) {
        this.seatingService.clearSeatingObjectArray();
      }

      this.stage.batchDraw();
    };

    /**
     * Returns true if two display objects collide/overlap,
     * otherwise false
     */
    (stage as ExtendedKonvaStage).checkCollision = (
      display1: Konva.Node,
      display2: Konva.Node
    ): boolean => {
      // get bounding rect of the display shapes
      const s1 = display1.getClientRect();
      const s2 = display2.getClientRect();
      // overlapping rect collision test
      if (
        s1.x + s1.width < s2.x ||
        s2.x + s2.width < s1.x ||
        s1.y + s1.height < s2.y ||
        s2.y + s2.height < s1.y
      ) {
        return false;
      }
      return true;
    };

    function getSeatAndLocationData(seat: Konva.Node): {
      $data: SeatWithLocationDataType;
      locationData: PublicBaseLocationSerializer;
    } {
      const display = seat.getParent();
      return (
        seat && {
          ...seat.getAttr("data"),
          locationData: display.getAttr("locationData"),
        }
      );
    }

    /**
     * Handles selection of multiple seats from the
     * shift + click drag event
     */
    const calculateCollisions = debounce(() => {
      let seats: Konva.Node[] = [];
      // Get nodes that are seats and are in the view port
      const seatsAvailable = this.layers.seatsAvailable.find(
        (node: Konva.Node) =>
          node.name() === ASSIGNED_SEATING.SEAT.ID && node.getAttr("inViewPort")
      );
      for (let i = 0; i < seatsAvailable.length; i += 1) {
        const seat = seatsAvailable[i];
        const display = seat.findAncestor(".display");
        const location = display.findAncestor(".location");
        location.clearCache();
        const highlight = (display as Konva.Container).findOne(".highlight");

        const seatCollision: boolean = (
          stage as ExtendedKonvaStage
        ).checkCollision(seat, this.selectionRect);

        if (this.isItemAssigner()) {
          if (seatCollision) {
            // prevent highlight from flashing on full_purchase_required seats
            const isFPRSeat =
              getSeatAndLocationData(seat).locationData.full_purchase_required;
            if (!isFPRSeat) {
              highlight.opacity(1);
            }
            seats.push(seat);
          } else {
            // Remove highlight
            highlight.opacity(0);
          }
        } else if (!this.isItemAssigner()) {
          // Check if seat is inside selection rectangle
          if (seatCollision) {
            highlight.opacity(1);
            seats.push(seat);
          } else {
            // Remove highlight
            highlight.opacity(0);
          }
        }
      }

      // handle selection of full_purchase_required seats in the item assigner
      if (this.isItemAssigner()) {
        // Find if all seats in the selection are full_purchase_required
        const hasOnlyFPRSeats: boolean = seats.every((seat) => {
          return getSeatAndLocationData(seat).locationData
            .full_purchase_required;
        });

        // If only full purchase seats are selected, then select all seats from the related table(s)
        if (hasOnlyFPRSeats) {
          const newSeats: Konva.Node[] = [];
          const locationDataArray: number[] = [];

          // outer loop for all of the selected seats
          for (let j = 0; j < seats.length; j += 1) {
            const seat = seats[j];
            const locationToSelect = getSeatAndLocationData(seat).locationData;
            // inner loop to style any related seats correctly and add them to the selection array
            if (!locationDataArray.includes(locationToSelect.id)) {
              for (let k = 0; k < locationToSelect.seats.length; k += 1) {
                const seatToSelect = locationToSelect.seats[k];
                const fprSeat = this.getSeatDataAndPermissions(seatToSelect.id)
                  .$data.node as Konva.Node;

                const display = fprSeat.findAncestor(".display");
                const location = display.findAncestor(".location");
                const highlight = (display as Konva.Container).findOne(
                  ".highlight"
                );
                location.clearCache();
                highlight.opacity(1);

                newSeats.push(fprSeat);
              }
            }
            locationDataArray.push(locationToSelect.id);
          }
          // update seats array
          seats = newSeats;
        }
      }

      this.layers.seatsAvailable.batchDraw();

      if (seats.length) {
        this.seatFactory.handleSeatSelect(seats, this.layers.seatsAvailable);
      }
    }, 350);

    /**
     * Reverse coords if user drags left / up
     */
    function reverseSelectionCoords(
      r1: PositionType,
      r2: PositionType
    ): { x1: number; y1: number; x2: number; y2: number } {
      let r1x = r1.x;
      let r1y = r1.y;
      let r2x = r2.x;
      let r2y = r2.y;
      let d: number;
      if (r1x > r2x) {
        d = Math.abs(r1x - r2x);
        r1x = r2x;
        r2x = r1x + d;
      }
      if (r1y > r2y) {
        d = Math.abs(r1y - r2y);
        r1y = r2y;
        r2y = r1y + d;
      }
      return { x1: r1x, y1: r1y, x2: r2x, y2: r2y };
    }

    (stage as ExtendedKonvaStage).startSelectDrag = (
      posIn: PositionType
    ): void => {
      this.posStartSelection = { x: posIn.x, y: posIn.y };
      this.posNowSelection = { x: posIn.x, y: posIn.y };
    };

    (stage as ExtendedKonvaStage).updateSelectDrag = (
      posIn: PositionType
    ): void => {
      this.posNowSelection = { x: posIn.x, y: posIn.y };
      this.posRect = reverseSelectionCoords(
        this.posStartSelection,
        this.posNowSelection
      );
      this.selectionRect.x(this.posRect.x1);
      this.selectionRect.y(this.posRect.y1);
      this.selectionRect.width(this.posRect.x2 - this.posRect.x1);
      this.selectionRect.height(this.posRect.y2 - this.posRect.y1);
      this.selectionRect.visible(true);

      calculateCollisions();
      stage.batchDraw();
    };

    stage.on("contentMousedown", (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.shiftKey) {
        stage.draggable(false);
        this.toolMode = "selecting";
        (stage as ExtendedKonvaStage).startSelectDrag(
          this.getScaledPointerPosition()
        );
      }
    });

    stage.on("contentMousemove", (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.shiftKey) {
        if (this.toolMode === "selecting") {
          (stage as ExtendedKonvaStage).updateSelectDrag(
            this.getScaledPointerPosition()
          );
          this.isMultiSelection = true;
        }
      }
    });

    stage.on("contentMouseup", () => {
      this.toolMode = "";
      this.selectionRect.visible(false);
      stage.draggable(true);
      stage.batchDraw();
    });

    /**
     * Pinch to zoom
     */
    stage.getContent().addEventListener(
      "touchmove",
      (evt: TouchEvent) => {
        evt.preventDefault();
        this.isPinchingGesture = true;

        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];

        // grab the current pointer position of the user
        const touch = this.stage.getPointerPosition() as Konva.Vector2d;
        if (touch1 && touch2) {
          const dist = StageService.getDistance(
            {
              x: touch1.clientX,
              y: touch1.clientY,
            },
            {
              x: touch2.clientX,
              y: touch2.clientY,
            }
          );

          if (!this.lastDist || this.lastDist === 0) {
            this.lastDist = dist;
          }

          // user is pinching the screen, make stage stop moving
          this.stage.draggable(false);
          // calculate the new scale and position of the stage
          const newScale = this.stage.scaleX() * (dist / this.lastDist);

          // dont let zoom past max scale
          const scale = newScale < this.MAX_SCALE ? newScale : this.MAX_SCALE;

          const pointerPosScaled = this.getScaledPointerPosition();
          const newPos = {
            x: -(pointerPosScaled.x - touch.x / scale) * scale,
            y: -(pointerPosScaled.y - touch.y / scale) * scale,
          };

          this.stage.scale({ x: scale, y: scale });
          this.stage.position(newPos);

          // hides / shows proper layers
          // also culls / caches nodes
          this.manageLayers();
          this.stage.batchDraw();
          this.lastDist = dist;
        } else {
          this.stage.draggable(true);
        }
      },
      false
    );

    this.stage.getContent().addEventListener("touchend", () => {
      // setting last dist to 0 allows the screen to save the last zoom state
      // otherwise the ratio wil be HUGE, this is because we use dist / last dist to calc
      // the stage ratio
      this.lastDist = 0;
      this.isPinchingGesture = false;
    });

    // throttle with no options, executes function on next clock tick
    stage.container().addEventListener("wheel", throttle(this.handleScroll));
  }

  static getDistance(p1: PositionType, p2: PositionType): number {
    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
  }

  /**
   * Sets the mousemove function.
   * This function is called on the stage during the mouse move event.
   */
  setMouseMove(func: (input?: unknown) => unknown): void {
    this.handleMouseMove = func;
  }

  getScaledPointerPosition(): PositionType {
    // screen space to world space
    const pointerPosition = this.stage.getPointerPosition();
    if (!pointerPosition) {
      return { x: 0, y: 0 };
    }
    return {
      x: (pointerPosition.x - this.stage.attrs.x) / this.stage.attrs.scaleX,
      y: (pointerPosition.y - this.stage.attrs.y) / this.stage.attrs.scaleY,
    };
  }

  setStageSize(width: number, height: number): void {
    this.stage.width(width);
    this.stage.height(height);
  }

  /**
   * Used for waitlisted seats.
   * For rows: finds the max number of seats with the given item id that are in an unbroken row.
   * For tables: not supported.
   */
  countSeatsInLocation(segmentId: number, itemId: number) {
    const segmentNodes = this.layers.segments.findOne(
      "#segments-container"
    ).children;

    const segmentNode: ExtendedKonvaGroup = segmentNodes
      .toArray()
      .find((node: ExtendedKonvaGroup) => node.$segmentData.id === segmentId);

    let finalMaxLimit = 0;

    const rowType = SEATING_LOCATION_TYPE_MAP.TYPE_ROW.id;

    for (let i = 0; i < segmentNode.$segmentData.location_set.length; i += 1) {
      const loc_set = segmentNode.$segmentData.location_set[i];

      // check that the iterated seat and the clicked seat are both from the row location type
      const isRowLocationType =
        loc_set.type === rowType && loc_set.type === rowType;

      if (isRowLocationType) {
        let maxLimit = 0;
        let currLimit = 0;

        for (let j = 0; j < loc_set.seats.length; j += 1) {
          const seat = loc_set.seats[j];

          const hasPermOnSeat = Boolean(
            this.seatingService.seatingMap[seat.id]?.find(
              (p) => p.seatingItemTypeId === itemId
            )
          );

          if (hasPermOnSeat) {
            currLimit += 1;
          } else {
            currLimit = 0;
          }

          if (currLimit > maxLimit) {
            maxLimit = currLimit;
          }
        }

        if (maxLimit > finalMaxLimit) {
          finalMaxLimit = maxLimit;
        }
      }
    }

    return finalMaxLimit;
  }

  /**
   * Toggles showing all seats and locations for the entire map
   */
  toggleShowAllSeats = () => {
    this.displayAllSeats = !this.displayAllSeats;
    this.manageLayers();
    this.stage.clearSelections();
    this.stage.batchDraw();
  };

  /**
   * Scale stage to specified value.
   */
  zoomTo(newScale: number) {
    // Make sure new scale is within bounds
    if (newScale < this.MIN_SCALE || newScale > this.MAX_SCALE) {
      return;
    }

    const oldScale = this.stage.scaleX();

    // The point we want to remain "in place" after zooming
    // Here, we choose the stage center:
    const center = {
      x: this.stage.width() / 2,
      y: this.stage.height() / 2,
    };

    // Convert that center point to "stage space" coordinates
    const centerStageCoords = {
      x: (center.x - this.stage.x()) / oldScale,
      y: (center.y - this.stage.y()) / oldScale,
    };

    // Calculate the new position so the center stays fixed in place
    const newPos = {
      x: center.x - centerStageCoords.x * newScale,
      y: center.y - centerStageCoords.y * newScale,
    };

    // Animate both the scale and the position
    this.stage.to({
      easing: Konva.Easings.EaseIn,
      scaleX: newScale,
      scaleY: newScale,
      x: newPos.x,
      y: newPos.y,
      duration: 0.1,
      onFinish: () => {
        this.manageLayers();
        this.stage.batchDraw();
      },
    });
  }

  /**
   * Scale stage by a specified amount.
   */
  zoomBy(val: number): void {
    const newScale = this.stage.scaleX() * (1 + val);
    this.zoomTo(newScale);
  }

  /**
   * Move the view on the x axis by the given value
   */
  moveX(val: number): void {
    this.stage.x(this.stage.x() + val);
    this.stage.batchDraw();
  }

  /**
   * Move the view on the y axis by the given value
   */
  moveY(val: number): void {
    this.stage.y(this.stage.y() + val);
    this.stage.batchDraw();
  }

  /**
   * Gets the data associated with a given seat.
   */
  async getSeatData(
    seatId: number,
    seatData?: SeatWithLocationDataType
  ): Promise<SeatWithLocationDataType> {
    let returnData = null;
    const display: Konva.Container = this.layers.seatsAvailable.findOne(
      `#${SeatFactory.createSeatId(seatId)}`
    );

    // get seat data if the segment containing the seat is currently active
    // display will have a value that can be used to get seat.data and seat.locationData
    if (display) {
      const seat = display && display.findOne(".seat");
      returnData = seat && {
        ...seat.getAttr("data"),
        locationData: display.getAttr("locationData"),
      };
    }
    // get seat data if the segment containing the seat is currently hidden
    // this finds the segment where segment.identifier = seat.segment_identifier
    // and then queries for the location data inside of that segment
    else if (!display && seatData && seatData.segment_identifier) {
      const segmentNodes = this.layers.segments.findOne(
        "#segments-container"
      ).children;
      const segmentNode: ExtendedKonvaGroup = segmentNodes
        .toArray()
        .find(
          (node: ExtendedKonvaGroup) =>
            node.$segmentData.identifier === seatData.segment_identifier
        );
      const segmentId = segmentNode.$segmentData.id;

      returnData = this.seatingService
        .fetchSingleLocationData(segmentId)
        .then((locData) => {
          const data = seatData;
          const locationData = locData.find(
            (loc) => loc.identifier === seatData.location_identifier.toString()
          );
          return { ...data, locationData };
        });
    }

    return returnData;
  }

  /**
   * Gets the data and permissions associated with a given seat.
   */
  getSeatDataAndPermissions(seatId: number): {
    $data: SeatWithLocationDataType;
    $permissions: MapPermissionsType[];
  } {
    const display: Konva.Container = this.layers.seatsAvailable.findOne(
      `#${SeatFactory.createSeatId(seatId)}`
    );
    const seat = display && display.findOne(".seat");
    const seatPermissions = this.seatingService.seatingMap[seatId] || [];

    return (
      seat && {
        $data: {
          ...seat.getAttr("data"),
          node: seat,
          locationData: display.getAttr("locationData"),
        },
        $permissions: seatPermissions,
      }
    );
  }

  setLocationSelected(
    data: {
      seat?: SeatWithLocationDataType;
      location?: TicketItemGroupPermissionTypeLocation;
    },
    selected: boolean,
    cb = undefined,
    hideCheckmark = false
  ): void {
    const { seat, location } = data;

    if (seat) {
      const node: Konva.Group = this.layers.seatsAvailable.findOne(
        `#${SeatFactory.createSeatId(seat.id)}`
      );
      if (node) {
        this.seatFactory.setSeatSelected(
          node,
          selected,
          this.layers.seatsAvailable,
          {
            animate: true,
            pixelRatio: this.getPixelRatio(),
            cb,
            hideCheckmark,
          }
        );
      }
    }

    if (location) {
      const node: Konva.Group = this.layers.seatsAvailable.findOne(
        `#${GeneralAdmissionFactory.createLocationId(location.id)}`
      );
      if (node) {
        this.generalAdmissionFactory.setLocationSelected(
          node,
          selected,
          this.layers.seatsAvailable
        );
      }
    }
  }

  /**
   * Mutate the given point to align it to the closest point on the map grid
   */
  getGridCoords(pos: PositionType): PositionType {
    const newPos: PositionType = { x: 0, y: 0 };
    newPos.x = Math.round(pos.x / this.GRID_WIDTH) * this.GRID_WIDTH;
    newPos.y = Math.round(pos.y / this.GRID_WIDTH) * this.GRID_WIDTH;
    return newPos;
  }

  /**
   * Updates the map to only enable GA locations based on the "enable" input param and the input item ID array.
   * Otherwise disables all GA locations on the map.
   *
   * Pass all of the event's item type IDs and set enable = true to reset the map back to normal.
   */
  enableGAByItemType(enable: boolean, itemIdArray: number[]): void {
    const generalAdmissionsToUpdate: Konva.Node[] = Object.values(
      this.layers.seatsAvailable.find(
        (node: Konva.Node) =>
          node.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID
      )
    );

    // Loop through every general admission location and update the color
    generalAdmissionsToUpdate.forEach((location) => {
      if (location.findAncestor) {
        let permissions: MapPermissionsType[] = [];

        if (enable) {
          permissions = this.seatingService.locationMap[
            location.attrs.data.id
          ].filter((p) => itemIdArray.includes(p.seatingItemTypeId));
          // allows ga location to be clicked
          location.setAttr("isActive", true);
        } else {
          permissions = [];
          // prevents ga location from being clicked on
          location.setAttr("isActive", false);
        }

        // clear each location cache
        const display = location.findAncestor(".display");
        const locationAncestor = display.findAncestor(".location");
        locationAncestor.clearCache();

        this.generalAdmissionFactory.updateLocationColor(location, permissions);

        if (this.displayAllSeats) {
          display.getParent().cache({ pixelRatio: this.getPixelRatio() });
        }
      }
    });

    // force redraw
    this.stage.batchDraw();
  }

  /**
   * Updates the map to only enable seats with the item ID(s) passed into the function.
   * Disables all other seats on the map.
   *
   * Pass all of the event's item type IDs to reset the map back to normal.
   */
  enableSeatsByItemType(itemIdArray: number[]): void {
    // get all seats on the map that do not have the input item type
    const seatsToDisable: Konva.Node[] = Object.values(
      this.layers.seatsAvailable.find(
        (node: Konva.Node) =>
          node.name() === ASSIGNED_SEATING.SEAT.ID &&
          this.seatingService.seatingMap[node.attrs.data.id] &&
          this.seatingService.seatingMap[node.attrs.data.id].find(
            (p) => !itemIdArray.includes(p.seatingItemTypeId)
          )
      )
    );

    // get all seats on the map that are assigned the input item type
    const seatsToEnable: Konva.Node[] = Object.values(
      this.layers.seatsAvailable.find(
        (node: Konva.Node) =>
          node.name() === ASSIGNED_SEATING.SEAT.ID &&
          this.seatingService.seatingMap[node.attrs.data.id] &&
          this.seatingService.seatingMap[node.attrs.data.id].find((p) =>
            itemIdArray.includes(p.seatingItemTypeId)
          )
      )
    );

    // Loop through every disabled seat and update the color
    // use for() loop to iterate over seat nodes
    for (let i = 0; i < seatsToDisable.length; i += 1) {
      const seat = seatsToDisable[i];
      if (seat.findAncestor) {
        const permissions: MapPermissionsType[] = [];

        // prevents seat from being clicked on
        seat.setAttr("isActive", false);

        // clear each location cache
        const display = seat.findAncestor(".display") as Konva.Group;
        const location = display.findAncestor(".location");
        location.clearCache();

        this.seatFactory.updateSeatColor(display, seat, permissions);

        if (this.displayAllSeats) {
          display.getParent().cache({ pixelRatio: this.getPixelRatio() });
        }
      }
    }

    // Loop through every enabled seat and update the color
    // use for() loop to iterate over seat nodes
    for (let i = 0; i < seatsToEnable.length; i += 1) {
      const seat = seatsToEnable[i];
      if (seat.findAncestor) {
        const permissions = this.seatingService.seatingMap[
          seat.attrs.data.id
        ].filter((p) => itemIdArray.includes(p.seatingItemTypeId));

        // allows seat to be clicked on
        seat.setAttr("isActive", true);

        // clear each location cache
        const display = seat.findAncestor(".display") as Konva.Group;
        const location = display.findAncestor(".location");
        location.clearCache();

        this.seatFactory.updateSeatColor(display, seat, permissions);

        if (this.displayAllSeats) {
          display.getParent().cache({ pixelRatio: this.getPixelRatio() });
        }
      }
    }

    // force redraw
    this.stage.batchDraw();
  }

  /**
   * Updates the map to only enable seats with the seat ID(s) passed into the function.
   * Disables all other seats on the map.
   *
   * Pass an empty array to reset the map back to normal.
   */
  enableSeatsById(seatIdArray: number[]): void {
    if (seatIdArray.length > 0) {
      this.isSeatFilterEnabled = true;
      // get all seats on the map that do not have the input item type
      const seatsToDisable: Konva.Node[] = Object.values(
        this.layers.seatsAvailable.find(
          (node: Konva.Node) =>
            node.name() === ASSIGNED_SEATING.SEAT.ID &&
            !seatIdArray.includes(node.attrs.data.id)
        )
      );

      // Loop through every disabled seat and update the color
      // use for() loop to iterate over seat nodes
      for (let i = 0; i < seatsToDisable.length; i += 1) {
        const seat = seatsToDisable[i];
        if (seat.findAncestor) {
          const permissions: MapPermissionsType[] = [];

          // prevents seat from being clicked on
          seat.setAttr("isActive", false);

          // clear each location cache
          const display = seat.findAncestor(".display") as Konva.Group;
          const location = display.findAncestor(".location");
          location.clearCache();

          this.seatFactory.updateSeatColor(display, seat, permissions);

          if (this.displayAllSeats) {
            display.getParent().cache({ pixelRatio: this.getPixelRatio() });
          }
        }
      }
    } else {
      // get all seats on the map
      const seatsToEnable: Konva.Node[] = Object.values(
        this.layers.seatsAvailable.find(
          (node: Konva.Node) => node.name() === ASSIGNED_SEATING.SEAT.ID
        )
      );

      // Loop through every enabled seat and update the color
      // use for() loop to iterate over seat nodes
      for (let i = 0; i < seatsToEnable.length; i += 1) {
        const seat = seatsToEnable[i];
        if (seat.findAncestor) {
          const permissions =
            this.seatingService.seatingMap[seat.attrs.data.id];

          // allows seat to be clicked on
          seat.setAttr("isActive", true);

          // clear each location cache
          const display = seat.findAncestor(".display") as Konva.Group;
          const location = display.findAncestor(".location");
          location.clearCache();

          this.seatFactory.updateSeatColor(display, seat, permissions);

          if (this.displayAllSeats) {
            display.getParent().cache({ pixelRatio: this.getPixelRatio() });
          }
        }
      }
      this.isSeatFilterEnabled = false;
    }

    // force redraw
    this.stage.batchDraw();
  }

  refreshSeatColors(itemTypeId: number): void {
    // get all seats on the map that are assigned a particular item type
    const seatArray: Konva.Node[] = Object.values(
      this.layers.seatsAvailable.find(
        (node: Konva.Node) =>
          node.name() === ASSIGNED_SEATING.SEAT.ID &&
          this.seatingService.seatingMap[node.attrs.data.id] &&
          this.seatingService.seatingMap[node.attrs.data.id].find(
            (p) => p.seatingItemTypeId === itemTypeId
          )
      )
    );

    // Loop through every seat and update the color
    for (let i = 0; i < seatArray.length; i += 1) {
      const seat = seatArray[i];
      if (seat.findAncestor) {
        const permissions =
          this.seatingService.seatingMap[seat.attrs.data.id] || [];

        // clear each location cache
        const display = seat.findAncestor(".display") as Konva.Group;
        const location = display.findAncestor(".location");
        location.clearCache();

        this.seatFactory.updateSeatColor(display, seat, permissions);

        if (this.displayAllSeats) {
          display.getParent().cache({ pixelRatio: this.getPixelRatio() });
        }
      }
    }
    // force redraw
    this.stage.batchDraw();
  }

  private createGANodeClone(
    gaNode: Konva.Node,
    style: {
      name: string;
      colorFill: string;
      colorStroke: string;
      strokeWidth: number;
      opacity: number;
    }
  ): {
    gaGroup: Konva.Group;
    gaNodeClone: Konva.Node;
  } {
    const bestAvailableStyle = style;
    const gaGroup = new Konva.Group();

    // Clone and style the GA node
    const gaNodeClone = gaNode.clone();
    gaNodeClone.setAttr("fill", bestAvailableStyle.colorFill);
    gaNodeClone.setAttr("stroke", bestAvailableStyle.colorStroke);
    gaNodeClone.setAttr("isBestAvailableSelected", true);
    gaNodeClone.setAttr("isActive", false);

    // Calculate position and rotation relative to stage
    const absolutePos = gaNode.getAbsolutePosition();
    const absoluteRotation = gaNode.getAbsoluteRotation();
    const gaPositionRelativeToStage = {
      x: (absolutePos.x - this.stage.x()) / this.stage.scaleX(),
      y: (absolutePos.y - this.stage.y()) / this.stage.scaleY(),
    };
    gaNodeClone.setAttr("x", gaPositionRelativeToStage.x);
    gaNodeClone.setAttr("y", gaPositionRelativeToStage.y);
    gaNodeClone.setAttr("rotation", absoluteRotation);
    gaGroup.add(gaNodeClone);

    // Handle label if it exists
    const display = gaNode.findAncestor(".display");
    const label = (display as Konva.Container).findOne(".label");
    if (label) {
      const labelAbsolutePos = label.getAbsolutePosition();
      const labelAbsoluteRotation = label.getAbsoluteRotation();
      const gaLabelRelativeToStage = {
        x: (labelAbsolutePos.x - this.stage.x()) / this.stage.scaleX(),
        y: (labelAbsolutePos.y - this.stage.y()) / this.stage.scaleY(),
      };
      const labelClone = label.clone();
      labelClone.setAttr("x", gaLabelRelativeToStage.x);
      labelClone.setAttr("y", gaLabelRelativeToStage.y);
      labelClone.setAttr("rotation", labelAbsoluteRotation);
      labelClone.setAttr(
        "fill",
        ASSIGNED_SEATING.SEAT.STYLE.BEST_AVAILABLE_GA_TEXT.colorFill
      );
      gaGroup.add(labelClone);
    }

    return { gaGroup, gaNodeClone };
  }

  async handleGaVisualization({
    formattedSelections,
    data,
  }: {
    formattedSelections: {
      permissionIds?: number[];
      membershipIds?: number[];
      itemTypeIds: number[];
      qtyMap?:
        | Record<string | number, number>
        | Record<string | number, never>
        | undefined;
      name: string;
      id: number;
      segment_identifier: string;
      identifier: string;
      qty?: number;
      price?: string[];
    };
    data?: LocationDataType;
  }): Promise<void> {
    this.showCheckmarkOnSeat = false;
    const gaNode = this.layers.seatsAvailable.findOne(
      (node: Konva.Node) =>
        node.name() === ASSIGNED_SEATING.GENERAL_ADMISSION.ID &&
        (node.attrs.data.id === data?.id ||
          node.attrs.data.id === formattedSelections.id)
    );

    if (gaNode) {
      const { permissionIds, itemTypeIds } = formattedSelections;
      const filteredItemTypeIds = itemTypeIds.filter((item) => item !== null);

      if (
        permissionIds &&
        permissionIds.length > 0 &&
        filteredItemTypeIds.length
      ) {
        permissionIds.forEach((permissionId, index) => {
          this.handleGANodeVisualization({
            gaNode,
            id: formattedSelections.id || data?.id,
            identifier: data?.identifier || formattedSelections.identifier,
            segment_identifier:
              data?.section || formattedSelections.segment_identifier,
            itemTypeId: filteredItemTypeIds[index],
            qty:
              formattedSelections.qtyMap?.[filteredItemTypeIds[index]] ??
              formattedSelections.qty,
            name: formattedSelections.name,
            permissionId,
            membershipLevelId: filteredItemTypeIds[index],
            data,
            price: formattedSelections.price?.[index],
          });
        });
      }
    }
  }

  private handleGANodeVisualization(params: {
    gaNode: Konva.Node;
    id?: number;
    identifier: string;
    segment_identifier: string;
    itemTypeId: number;
    qty?: number;
    name: string;
    permissionId?: number;
    membershipLevelId?: number;
    data?: LocationDataType;
    price?: string;
  }): void {
    this.lastSelectionWasIndividual = true;
    const { gaGroup, gaNodeClone } = this.createGANodeClone(
      params.gaNode,
      ASSIGNED_SEATING.SEAT.STYLE.BEST_AVAILABLE_CHOSEN
    );

    // This is not ideal for GA ids, but the id is not unique for locations.
    // Since its the location id. This is a way to ensure that the GA item types id is unique
    // Needed for processing Konva Nodes regarding GA (removing/editing)
    // I.e -> One GA location might have multiple item types, but since they are from the same location, they
    // have the same id. They are not like seats, which each have a unique id associated with them.
    const baseId = params.itemTypeId;
    gaNodeClone.setAttr("label", baseId);
    gaNodeClone.setAttr("id", baseId);
    gaNodeClone.setAttr("isActive", true);

    // Calculate proper zoom position and scale for the GA node, similar to segment focusing
    const zoomPosition = this.calculateZoomPosition(params.gaNode, 200);

    this.stage.to({
      duration: 1,
      x: zoomPosition.position.x,
      y: zoomPosition.position.y,
      scaleX: zoomPosition.scale,
      scaleY: zoomPosition.scale,
      easing: Konva.Easings.EaseInOut,
      onFinish: () => {
        this.manageLayers(true);
        this.stage.batchDraw();
      },
    });

    gaNodeClone.setAttr("isBestAvailableSelected", true);
    this.layers.mainLayer.add(gaGroup);
    this.seatingService.addSeatToSeatingObjectArray({
      id: baseId,
      identifier: params.identifier,
      segment_identifier: params.segment_identifier,
      qty: params.qty,
      is_accessible: false,
      is_limited_visibility: false,
      location_identifier: "",
      ticketTypeId: params.itemTypeId,
      permissionId: params.permissionId,
      membershipLevelId: params.membershipLevelId,
      name: params.name,
      data: params.data,
      price: params.price,
    });
  }

  async handleSeatingVisualization({
    seats,
    selectedIndividually = false,
    onSelect,
    isMobile = false,
    cb,
    shouldZoom = false,
  }: {
    seats: SeatHighlightData;
    selectedIndividually?: boolean;
    onSelect?: () => void;
    isMobile?: boolean;
    cb?: () => void;
    shouldZoom?: boolean;
  }): Promise<void> {
    this.showCheckmarkOnSeat = false;
    if (onSelect) {
      onSelect();
    }
    const segmentGroup = this.findSegmentGroup(
      seats.segmentIdentifier
    ) as ExtendedKonvaContainer;
    if (!segmentGroup) return;

    // Wait until the map is loaded before displaying the seats in mobile
    // Otherwise we need to click twice to display the seats
    // We don't need this for desktop views since its taken care of during hover.
    if (this.shouldNotDisplayAllSeats() && isMobile) {
      if (this.activeSegment !== segmentGroup.$segmentData?.id) {
        await this.handleDisplaySeats(
          (segmentGroup as ExtendedKonvaContainer).$segmentData
        );
      }
    }

    // For individual seat selections, combine existing selected seat IDs with new ones
    // For best available list selections, use only the new seat IDs (start fresh)
    let seatsToFocus = seats;
    if (!shouldZoom && seats.seatIds) {
      const existingSelectedSeatIds =
        this.seatingService.seatingObjectArray.map((seat) => seat.id);
      const allSeatIds = [
        ...new Set([...existingSelectedSeatIds, ...seats.seatIds]),
      ];
      seatsToFocus = {
        ...seats,
        seatIds: allSeatIds,
      };
    }

    this.focusBySeatIds(
      seatsToFocus,
      {
        scale: this.scaleForHighlight,
        animated: true,
        selectedIndividually,
        cb,
        shouldZoom, // Use the shouldZoom parameter passed to this method
      },
      true,
      true
    );
  }
}
